/**
 * Web port of mobile components/diary/day-band.ts — identical behaviour.
 *
 * A day's colour — the one rule behind every per-day dot: the Diary week
 * strip, the Progress week card and the Progress "Your days" grid.
 *
 * Each surface judges a day with `judgeDay` (progress/heatmapGrid.ts) over its
 * day score (spec §4a, ./today-score.ts) — today from the live diary, any
 * other day from its dailySummaries doc — and shows the band:
 * Safe → green, Moderate → yellow, High → red; drinks-only and empty days get
 * no dot. The `performance` field on a dailySummaries doc is written with the
 * same rule (`dayPerformance`), but nothing reads it back to colour a day: an
 * older doc may still hold the retired "count budgets over" dot.
 */

import { judgeDay, type HeatmapCellState, type HeatmapDay } from '@/progress/heatmapGrid'
import { computeTodayScore, scoreStoredDay } from '@/diary/today-score'
import type { DailySummary, DailySummaryDoc, DayPerformance, DiaryEntry, HealthProfile } from '@/types'

/** A day's dot for its band (empty and drinks-only days get no colour). */
export const DAY_DOT: Record<HeatmapCellState, DayPerformance> = {
  safe: 'green',
  moderate: 'yellow',
  high: 'red',
  drinks: 'none',
  empty: 'none',
  locked: 'none',
  future: 'none',
}

/** The dot for a day (null / undefined = nothing logged). */
export function dayDot(day: HeatmapDay | null | undefined): DayPerformance {
  return DAY_DOT[judgeDay(day).state]
}

/** A day from the live diary: its summary + entries, as the home hero scores it. */
export function liveHeatmapDay(summary: DailySummary | null, entries: DiaryEntry[]): HeatmapDay {
  return { totalItems: entries.length, score: computeTodayScore(summary, entries) }
}

/**
 * The persisted dailySummaries `performance` — mobile DiaryContext
 * `computeDayPerformance`: the day's band from the same live-day input.
 */
export function dayPerformance(summary: DailySummary | null, entries: DiaryEntry[]): DayPerformance {
  return dayDot(liveHeatmapDay(summary, entries))
}

/**
 * Today from the live diary — only while the diary shows today and its
 * entries for today have loaded (otherwise today is read from its doc).
 */
export function liveTodayDay(
  diary: { selectedDate: string; ready: boolean; summary: DailySummary | null; entries: DiaryEntry[] },
  today: string,
): HeatmapDay | null {
  if (diary.selectedDate !== today || !diary.ready || !diary.summary || diary.summary.date !== today) return null
  return liveHeatmapDay(diary.summary, diary.entries)
}

/** A stored day (its dailySummaries doc; undefined = no doc = nothing logged). */
export function storedHeatmapDay(
  doc: DailySummaryDoc | undefined,
  limits: Parameters<typeof scoreStoredDay>[1],
): HeatmapDay | null {
  return doc ? { totalItems: doc.totalItems || 0, score: scoreStoredDay(doc, limits) } : null
}

/**
 * The user's current daily budgets (spec §3): doctor-set limits first, else
 * the condition-calculated budgets (protein = proteinPerKg × body weight).
 * Ceilings: calories/addedSugar/satFat/sodium. Targets: fiber/protein. Fluid
 * is a hydration GOAL (default 2000ml), not a restriction. A stored day
 * without its own ceilings is judged against these.
 */
export function currentDayLimits(healthProfile: HealthProfile | undefined) {
  const calc = healthProfile?.calculatedLimits
  const userWeight = healthProfile?.weight || 70
  return {
    calories: healthProfile?.dailyCalorieLimit || calc?.calories || 2000,
    addedSugarsG: healthProfile?.dailyAddedSugarLimit || calc?.addedSugarG || 36,
    saturatedFatG: healthProfile?.dailySaturatedFatLimit || calc?.saturatedFatG || 22,
    fiberG: healthProfile?.dailyFiberTarget || calc?.fiberG || 28,
    sodiumMg: healthProfile?.dailySodiumLimit || calc?.sodiumMg || 2300,
    proteinG:
      healthProfile?.dailyProteinTarget ||
      (calc?.proteinPerKg ? Math.round(calc.proteinPerKg * userWeight) : Math.round(0.8 * userWeight)),
    fluidMl: healthProfile?.dailyFluidGoal || 2000,
    proteinIsTarget: calc?.proteinIsTarget ?? false,
  }
}
